import React from "react";
import { useMap } from "react-leaflet";
import L from "leaflet";
import * as turf from "@turf/turf";
import { Button, Modal, Table } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { useLocation } from "react-router-dom";
import { shallowEqual } from "react-redux";

import variableColors from "../_App.module.scss";
import { useAppDispatch, useAppSelector } from "../redux/hooks";
import {
  dispatchCoordinates,
  dispatchStopFetching,
  fetchDataActiveBus,
  resetDataActiveBus,
  setActiveBus,
} from "../redux/actions";

const { pinkColor, indigoColor } = variableColors;

const StyledSelectBus = styled.div`
  border: none;
`;

const MapSelectBus = ({
  busesToShow,
  remainingBuses,
}: {
  busesToShow: {
    busesOnTime: BusInfo[];
    busesTooFast: BusInfo[];
    delayedBuses: BusInfo[];
    remainingBuses: BusInfo[];
    busesWithLayover: BusInfo[];
  };
  remainingBuses: BusInfo[];
}): JSX.Element => {
  const leafletMap = useMap();
  const { t } = useTranslation();
  const { pathname: pathName } = useLocation();

  const dispatch: Dispatch = useAppDispatch();

  const [currentZoomFromRedux, circleRadiusSettings]: [number, number] = useAppSelector(
    (state: RootState) => [state?.constantValues?.currentZoom, state?.constantValues?.mapSettings?.circleRadiusSettings],
    shallowEqual
  );
  // console.log("circleRadiusSettings:", circleRadiusSettings);

  const [showModal, setShowModal] = React.useState<boolean>(false);
  const [selectedBuses, setSelectedBuses] = React.useState<BusInfo[]>([]);
  // console.log("selectedBuses:", selectedBuses);

  React.useEffect(() => {
    if (showModal) {
      dispatch(dispatchStopFetching(true));
    } else if (showModal === false) {
      dispatch(dispatchStopFetching(false));
    }
  }, [dispatch, showModal]);

  //* Reset buses
  const resetBuses = (): void => setSelectedBuses([]);

  // const handleShow = (): void => setShowModal(true);
  //* Close Modal
  const handleCloseModal = (): void => {
    setShowModal(false);
    resetBuses();
  };

  //* BusActions
  const busActions = React.useCallback(
    (bus: BusInfo): void => {
      // console.log("bus:", bus);
      dispatch(setActiveBus(bus?.id));

      if (pathName === "/") {
        const { routeId, direction } = bus || {};
        if (routeId && direction) {
          dispatch(fetchDataActiveBus(routeId, direction));
        } else {
          dispatch(resetDataActiveBus());
        }
        if (currentZoomFromRedux) {
          dispatch(dispatchCoordinates(bus?.loc?.lat, bus?.loc?.lon, currentZoomFromRedux));
        }
      }

      setShowModal(false);
      resetBuses();
    },
    [currentZoomFromRedux, dispatch, pathName]
  );

  //* Close modal if no selectedRoutes
  React.useEffect(() => {
    if (selectedBuses?.length === 0) {
      setShowModal(false);
    }
  }, [selectedBuses]);

  //* Selecting nearest buses
  React.useEffect(() => {
    if (!leafletMap || !busesToShow || !remainingBuses) return;

    if (leafletMap && busesToShow && remainingBuses) {
      const allMapBuses: BusInfo[] = [
        ...busesToShow?.busesOnTime,
        ...busesToShow?.busesTooFast,
        ...busesToShow?.busesWithLayover,
        ...busesToShow?.delayedBuses,
        ...remainingBuses,
      ];
      // console.log("allMapBuses:", allMapBuses);

      leafletMap.on("click", async (event: L.LeafletMouseEvent) => {
        await resetBuses();

        const busCircleRadius: number = circleRadiusSettings * 1000;

        const { lat, lng } = event?.latlng;
        // console.log({ lat, lng });
        const circleMarker = L.circle([lat, lng], {
          radius: busCircleRadius,
          stroke: true,
          weight: 3,
          fillColor: pinkColor,
          color: indigoColor,
          opacity: 1,
          fillOpacity: 0.2,
        }).addTo(leafletMap);

        let turfCircle = turf.circle([lng, lat], busCircleRadius, { steps: 12, units: "meters" });
        // console.log("turfCircle:", turfCircle);

        // Convert bus positions to Turf points (assuming busPositions = [{lat, lng}, ...])
        const busPoints = allMapBuses?.map((bus: BusInfo) => turf.point([bus?.loc?.lon, bus?.loc?.lat], { id: bus?.id }));
        // console.log("busPoints:", busPoints);

        // Filter buses inside circle
        const busesIdsInCircle: string[] = busPoints
          ?.filter((point) => turf.booleanPointInPolygon(point, turfCircle))
          ?.map((point) => point.properties?.id);
        // console.log("busesIdsInCircle:", busesIdsInCircle);

        const selectedBusesToProceed: BusInfo[] = allMapBuses
          ?.filter((bus: BusInfo) => busesIdsInCircle?.includes(bus?.id))
          ?.sort((a: BusInfo, b: BusInfo) => a?.vehicleName?.localeCompare(b?.vehicleName) || a?.id?.localeCompare(b?.id));
        // console.log("selectedBusesToProceed:", selectedBusesToProceed);

        if (selectedBusesToProceed?.length === 1) {
          busActions(selectedBusesToProceed?.[0]);
        } else if (selectedBusesToProceed?.length > 1) {
          // handleShow();
          setShowModal(true);
          setSelectedBuses(selectedBusesToProceed);
        }

        setTimeout(() => {
          circleMarker.removeFrom(leafletMap);
        }, 750);
      });

      return () => {
        resetBuses();
      };
    }
  }, [busActions, busesToShow, circleRadiusSettings, leafletMap, remainingBuses]);

  const availableBuses: JSX.Element[] = selectedBuses?.map((bus: BusInfo, index: number): JSX.Element => {
    return (
      <tr key={index} onClick={() => busActions(bus)}>
        <td className="text-muted">{index + 1}</td>
        <td className="span_bold">{bus?.vehicleName || bus?.id || t("b/d")}</td>
        <td
          style={{
            color: bus?.routeInfoAdded?.textColor,
            backgroundColor: bus?.routeInfoAdded?.color,
            fontWeight: "bolder",
            textAlign: "center",
            borderRight: "none",
          }}
        >
          {bus?.acronym || bus?.routeShortName || bus?.routeId || t("b/d")}
        </td>
        <td>
          <span className="span_maroon span_bold">[{bus?.direction}]</span> {bus?.headsign || t("b/d")}
        </td>
      </tr>
    );
  });

  return (
    <React.Fragment>
      <Modal
        show={showModal}
        fullscreen={"lg-down"}
        onHide={handleCloseModal}
        dialogClassName=""
        centered={true}
        backdrop={true}
        keyboard={true}
        size={"lg"}
        scrollable={true}
      >
        <Modal.Header closeButton={true} className="bg-secondary text-light" closeVariant={"white"}>
          <Modal.Title>{t("Wybierz pojazd")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <StyledSelectBus>
            <Table striped bordered hover className="bus_table">
              <thead>
                <tr>
                  <th>{"#"}</th>
                  <th>{t("Pojazd")}</th>
                  <th className="text-center">{t("Linia")}</th>
                  <th>{t("Kierunek kursu / Dokąd")}</th>
                </tr>
              </thead>
              <tbody>{availableBuses}</tbody>
            </Table>
          </StyledSelectBus>
        </Modal.Body>
        <Modal.Footer className="bg-secondary text-light">
          <Button variant="primary" onClick={handleCloseModal}>
            {t("Zamknij")}
          </Button>
        </Modal.Footer>
      </Modal>
    </React.Fragment>
  );
};

export default MapSelectBus;
